import saveAs from 'file-saver';
import React, { useMemo } from 'react';
import BeaconChartWithLegend, {
  BeaconChartWithLegendProps
} from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import NoDataPlaceHolder from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import SplineChart from 'src/components/BeaconV2Components/SplineChart/SplineChart';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import convertLineChartSeriesToDelimitedData from 'src/components/Charts/GenericChart/SeriesConverters/lineChart';
import { useAtlasV2MetricByWeekId } from 'src/serverProxy/useAtlasV2MetricByWeekId';
import { useChartMetricByWeekId } from 'src/serverProxy/useChartMetricByWeekId';
import { useDorisMetricByWeekId } from 'src/serverProxy/useDorisMetricByWeekId';
import { formatMetric } from 'src/utils/app';
import { calculateDerivedMetricByWeekId } from 'src/utils/chartUtils';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { ComparisonPeriod, useAppSelector, WeekRange } from 'src/utils/Hooks/typedHooks';
import { DORIS_API_METRIC_TYPE } from 'src/utils/constants';

const transformUnitsSoldData = (rows) => rows.map((row) => [row.weekId, row.unitsSold_sum_value]);

const transformTrafficData = (records) => records?.map((row) => [row.weekId, row.value]) || [];

// This will be used in keyMetricCard, so we took out the logic into hooks.
export const useConversionV2ChartData = () => {
  // Use mainTimePeriod.id from Redux for consistency with useChartMetricByWeekId
  // which also uses Redux state. This prevents sync issues between URL and Redux state.
  const mainTimePeriodId = useAppSelector((state) => state.mainTimePeriod.id);
  const isLastWeek = mainTimePeriodId === WeekRange.LAST_WEEK;
  const {
    mainRowsWithWeekId: unitsSoldPrimaryRows,
    comparisonRowsWithWeekId: unitsSoldComparisonRows,
    isLoading: loadingUnitsSold
  } = useAtlasV2MetricByWeekId({
    indexName: 'sales',
    fieldName: 'unitsSold'
  });

  const { response: totalTrafficDorisResponse, isLoading: loadingTotalTraffic } = useDorisMetricByWeekId({
    queryKey: 'doris-total-traffic',
    metricType: DORIS_API_METRIC_TYPE.TOTAL_TRAFFIC,
    resultCount: 120
  });
  // Transform units sold data
  const unitsSoldPrimaryData = transformUnitsSoldData(unitsSoldPrimaryRows);
  const unitsSoldComparisonData = transformUnitsSoldData(unitsSoldComparisonRows);

  // Transform traffic data
  const { primary: trafficPrimaryResponse, comparison: trafficComparisonResponse } = totalTrafficDorisResponse;
  const trafficPrimaryData = transformTrafficData(trafficPrimaryResponse?.records);
  const trafficComparisonData = transformTrafficData(trafficComparisonResponse?.records);

  const conversionRatePrimaryData = calculateDerivedMetricByWeekId({
    dataSet1: unitsSoldPrimaryData,
    dataSet2: trafficPrimaryData
  });
  const conversionRateComparisonData = calculateDerivedMetricByWeekId({
    dataSet1: unitsSoldComparisonData,
    dataSet2: trafficComparisonData
  });

  const {
    primarySeries,
    secondarySeries,
    primaryDerivedMetricAverage,
    comparisonDerivedMetricAverage,
    primaryDerivedMetricPercentChange,
    primaryLastWeekValue,
    comparisonLastWeekValue,
    primaryPercentChangeLastWeek
  } = useChartMetricByWeekId({
    primaryRawData: conversionRatePrimaryData,
    secondaryRawData: conversionRateComparisonData,
    primaryDerivedMetricRawData: {
      dataSource1: unitsSoldPrimaryData,
      dataSource2: trafficPrimaryData
    },
    secondaryDerivedMetricRawData: {
      dataSource1: unitsSoldComparisonData,
      dataSource2: trafficComparisonData
    }
  });

  return {
    loading: loadingUnitsSold || loadingTotalTraffic,
    primarySeries,
    secondarySeries,
    primaryDerivedMetricAverage: isLastWeek ? primaryLastWeekValue : primaryDerivedMetricAverage,
    comparisonDerivedMetricAverage: isLastWeek ? comparisonLastWeekValue : comparisonDerivedMetricAverage,
    primaryDerivedMetricPercentChange: isLastWeek ? primaryPercentChangeLastWeek : primaryDerivedMetricPercentChange
  };
};

/**
 * This component will display a spine chart of the conversion rate by week
 * It will use a combination of elastic search of unitsSold and doris traffic
 *
 */
const AtlasV2ConversionSplineV2Chart = () => {
  const { displayName: mainPeriodDisplayName } = useAppSelector((state) => state.mainTimePeriod);
  const { displayName: comparisonPeriodDisplayName, id: comparisonPeriodId } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );

  const {
    primarySeries,
    secondarySeries,
    loading,
    primaryDerivedMetricAverage,
    comparisonDerivedMetricAverage,
    primaryDerivedMetricPercentChange
  } = useConversionV2ChartData();

  const exportSplineChartToCsv = useMemo(
    () => () => {
      try {
        const series = [
          {
            name: mainPeriodDisplayName,
            data: primarySeries
          },
          {
            name: comparisonPeriodDisplayName,
            data: secondarySeries
          }
        ];

        const csvContent = convertLineChartSeriesToDelimitedData(
          series,
          comparisonPeriodId === ComparisonPeriod.PRIOR_PERIOD
        );

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, `Conversion-rate.csv`);
        return Promise.resolve();
      } catch (e) {
        console.error('Error exporting spline chart to CSV:', e);
        return Promise.reject(e);
      }
    },
    [mainPeriodDisplayName, primarySeries, comparisonPeriodDisplayName, secondarySeries, comparisonPeriodId]
  );

  const getBeaconChartWithLegendProps = () => {
    const defaultProps: Omit<BeaconChartWithLegendProps, 'children'> = {
      title: 'Conversion Rate',
      primaryLegendProps: {
        displayName: mainPeriodDisplayName,
        metric: formatMetric(primaryDerivedMetricAverage, METRICTYPE.PERCENT, {
          showFullValue: true,
          decimalPlaces: 2
        }),
        change: formatMetric(Math.abs(primaryDerivedMetricPercentChange), METRICTYPE.PERCENT, {
          showFullValue: true,
          decimalPlaces: 2
        }),
        direction: getDirection(primaryDerivedMetricPercentChange)
      },
      comparisonLegendProps: {
        displayName: comparisonPeriodDisplayName,
        metric: formatMetric(comparisonDerivedMetricAverage, METRICTYPE.PERCENT, {
          showFullValue: true,
          decimalPlaces: 2
        })
      },
      loading,
      loadingComponent: <SplineChartLoading />,
      showTitleSummary: true,
      convertSeriesToDelimitedData: () => exportSplineChartToCsv()
    };

    return defaultProps;
  };

  return (
    <BeaconChartWithLegend {...getBeaconChartWithLegendProps()}>
      <SplineChart
        primaryData={primarySeries}
        secondaryData={secondarySeries}
        metricType={METRICTYPE.PERCENT}
        NoDataPlaceholder={
          <NoDataPlaceHolder
            title="Data currently unavailable"
            description="There is currently no data matching your selected criteria. Please try another query to view insights."
            flexProps={{ margin: '80px auto' }}
          />
        }
      />
    </BeaconChartWithLegend>
  );
};

export default AtlasV2ConversionSplineV2Chart;
